<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, watch } from 'vue';

import InstallationModal from '~/components/shared/InstallationModal.vue';
import { InstallationRiskApi } from '~/services/installation-risk-api/installation-risk-api.types';

import useChangeInverterSerialNumber from '../queries/useChangeInverterSerialNumber';
import {
  type ChangeInverterSerialNumberForm,
  changeInverterSerialNumberSchema,
} from '../schemas/changeInverterSerialNumberSchema';

interface Props {
  financingId: number;
  inverters: InstallationRiskApi.InstalledInverter[];
  'invertersError'?: Error | null;
  'isLoadingInverters'?: boolean;
  modelValue: boolean;
  partnerId: number;
  projectId: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'success': [];
  'update:modelValue': [value: boolean];
}>();

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const changeSerialNumberMutation = useChangeInverterSerialNumber();

// Usar dados dos inversores recebidos via props
const inverters = computed(() => props.inverters || []);
const isLoadingInverters = computed(() => props['is-loading-inverters'] || false);
const invertersError = computed(() => props['inverters-error'] || null);

const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
  setFieldValue,
  setValues,
  values,
} = useForm<ChangeInverterSerialNumberForm>({
  initialValues: {
    inverters: [],
    ticket: '',
  },
  validateOnMount: false,
  validationSchema: toTypedSchema(changeInverterSerialNumberSchema),
});

const [ticket, ticketProps] = defineField('ticket');

// Watch inverters data to update form when it changes
watch(
  () => inverters.value,
  (newInverters) => {
    if (newInverters && newInverters.length > 0) {
      setValues({
        inverters: newInverters.map((inv) => ({
          installed_inverter_id: inv.id,
          serial_number_new: '',
        })),
        ticket: values.ticket || '',
      });
      return;
    }
    setValues({
      inverters: [],
      ticket: values.ticket || '',
    });
  },
  { immediate: true },
);

const handleSave = handleSubmit(async (formValues) => {
  const request: InstallationRiskApi.ChangeInverterSerialNumberRequest = {
    financing_id: props.financingId,
    items: formValues.inverters.map((inv) => {
      const inverter = inverters.value.find((i) => i.id === inv.installed_inverter_id);
      return {
        installed_inverter_id: inv.installed_inverter_id,
        new_serial_number: inv.serial_number_new.trim(),
        old_serial_number: inverter?.serial_number || '',
      };
    }),
    ticket: formValues.ticket?.trim() || undefined,
  };

  await changeSerialNumberMutation.mutateAsync({
    inverters: inverters.value,
    partnerId: props.partnerId,
    projectId: props.projectId,
    request,
  });
  emit('success');
  isVisible.value = false;
});

const handleCancel = () => {
  if (!changeSerialNumberMutation.isPending.value) {
    isVisible.value = false;
    resetForm();
  }
};

const isLoading = computed(() => changeSerialNumberMutation.isPending.value);

const canSave = computed(() => {
  // Não permitir salvar se estiver carregando
  if (isLoading.value) return false;
  if (isLoadingInverters.value) return false;

  // Não permitir salvar se houver erro ou não houver inversores
  if (invertersError.value) return false;
  if (!inverters.value || inverters.value.length === 0) return false;

  // Verificar se o formulário tem o mesmo número de inversores
  const formInverters = values.inverters;
  if (!formInverters || formInverters.length !== inverters.value.length) return false;

  // Verificar se todos os SNs novos estão preenchidos e válidos (mínimo 3 caracteres)
  const allValid = formInverters.every((inv) => {
    if (!inv || !inv.serial_number_new) return false;
    const serialNumber = String(inv.serial_number_new).trim();
    return serialNumber.length >= 3;
  });

  return allValid;
});

watch(isVisible, (newValue) => {
  if (!newValue) {
    resetForm();
  } else if (newValue && inverters.value.length > 0) {
    // Garantir que o formulário seja inicializado quando o modal abrir
    setValues({
      inverters: inverters.value.map((inv) => ({
        installed_inverter_id: inv.id,
        serial_number_new: '',
      })),
      ticket: '',
    });
  }
});
</script>

<template>
  <InstallationModal
    :loading="isLoading"
    :model-value="isVisible"
    :primary-button-disabled="!canSave"
    primary-button-text="Confirmar troca"
    title="Troca de Número de Série de Inversores Instalados"
    @primary-action="handleSave"
    @secondary-action="handleCancel"
    @update:model-value="isVisible = $event"
  >
    <div class="change-sn-modal">
      <div class="alert alert-info mb-3">
        <div class="alert-with-icon">
          <i class="icon ti ti-info-circle" />
          <div class="content">
            <strong>Informação importante</strong>
            <p class="mb-0">
              Esta ação alterará o número de série do(s) inversor(es) e atualizará automaticamente as bases técnicas.
              Certifique-se de que os números de série informados estão corretos.
            </p>
          </div>
        </div>
      </div>

      <div class="mb-3">
        <label for="ticket-number" class="form-label">
          Número do Ticket/Chamado
          <span class="text-muted">(opcional)</span>
        </label>
        <input
          id="ticket"
          v-model="ticket"
          v-bind="ticketProps"
          class="form-control"
          placeholder="Ex: ZENDESK-12345"
          type="text"
          :disabled="isLoading"
        />
        <small v-if="errors.ticket" class="text-danger">{{ errors.ticket }}</small>
      </div>

      <div v-if="isLoadingInverters" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="mt-2 text-muted">Carregando inversores...</p>
      </div>

      <div v-else-if="invertersError" class="alert alert-warning">
        <div class="alert-with-icon">
          <i class="icon ti ti-alert-triangle" />
          <div class="content">
            <strong>Erro ao carregar inversores</strong>
            <p class="mb-0">Não foi possível carregar as informações dos inversores. Tente novamente.</p>
          </div>
        </div>
      </div>

      <div v-else-if="!inverters || inverters.length === 0" class="alert alert-info">
        <div class="alert-with-icon">
          <i class="icon ti ti-info-circle" />
          <div class="content">
            <strong>Nenhum inversor encontrado</strong>
            <p class="mb-0">Não há inversores instalados para este projeto.</p>
          </div>
        </div>
      </div>

      <div v-else-if="values.inverters && values.inverters.length === inverters.length" class="inverters-list">
        <h6 class="mb-3">Inversores</h6>
        <div
          v-for="(inverter, index) in inverters"
          :key="inverter.id"
          class="inverter-item mb-4 p-3 border rounded"
        >
          <div class="row g-3">
            <div class="col-md-6">
              <label :for="`brand-${inverter.id}`" class="form-label text-muted small">Marca</label>
              <input
                :id="`brand-${inverter.id}`"
                :value="inverter.brand"
                class="form-control"
                disabled
                type="text"
              />
            </div>

            <div class="col-md-6">
              <label :for="`power-${inverter.id}`" class="form-label text-muted small">Potência</label>
              <input
                :id="`power-${inverter.id}`"
                :value="`${inverter.power_kw} kW`"
                class="form-control"
                disabled
                type="text"
              />
            </div>

            <div class="col-md-6">
              <label :for="`sn-old-${inverter.id}`" class="form-label text-muted small">SN Anterior</label>
              <input
                :id="`sn-old-${inverter.id}`"
                :value="inverter.serial_number"
                class="form-control bg-light"
                disabled
                type="text"
              />
            </div>

            <div class="col-md-6">
              <label :for="`sn-new-${inverter.id}`" class="form-label">
                Novo Número de Série
                <span class="text-danger">*</span>
              </label>
              <input
                :id="`sn-new-${inverter.id}`"
                :value="values.inverters[index]?.serial_number_new || ''"
                class="form-control"
                :class="{ 'is-invalid': errors[`inverters.${index}.serial_number_new`] }"
                placeholder="Digite o novo número de série"
                type="text"
                :disabled="isLoading"
                @input="(e) => {
                  const target = e.target as HTMLInputElement;
                  setFieldValue(`inverters.${index}.serial_number_new`, target.value);
                }"
              />
              <small
                v-if="errors[`inverters.${index}.serial_number_new`]"
                class="text-danger d-block mt-1"
              >
                {{ errors[`inverters.${index}.serial_number_new`] }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div v-if="errors.inverters" class="alert alert-danger">
        {{ errors.inverters }}
      </div>
    </div>
  </InstallationModal>
</template>

<style scoped lang="scss">
.change-sn-modal {
  .alert-with-icon {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;

    .icon {
      flex-shrink: 0;
      font-size: 1.25rem;
      margin-top: 0.125rem;
    }

    .content {
      flex: 1;

      strong {
        display: block;
        margin-bottom: 0.25rem;
      }

      p {
        font-size: 0.875rem;
      }
    }
  }

  .inverters-list {
    .inverter-item {
      background-color: #f8f9fa;
      border-color: #dee2e6 !important;

      .form-label {
        font-weight: 500;
        margin-bottom: 0.5rem;
      }

      .form-control:disabled {
        background-color: #e9ecef;
        color: #6c757d;
        cursor: not-allowed;
      }
    }
  }
}
</style>
